import React, { useState, useEffect } from "react";
import { Box, Button, TextField, TablePagination } from "@mui/material";
import FileDownloadOutlinedIcon from "@mui/icons-material/FileDownloadOutlined";
import moment from "moment-timezone";

import { endPoints } from "../../../constant/Environment";
import { getAllData } from "../../../Utility/API";
import PageHeader from "../../../components/PageHeader";
import SectionCard from "../../../components/SectionCard";
import Loader from "../../../components/Loader";
import { tokens } from "../../../theme";
import { mono, pill, th, td, TablePaginationActions } from "../../../components/tableKit";

const eventKind = (eventType) => {
  const s = String(eventType || "").toUpperCase();
  if (s.includes("LOGIN")) return "ok";
  if (s.includes("ABANDONED") || s.includes("FAILED")) return "bad";
  if (s.includes("DEPOSIT") || s.includes("CHECKOUT")) return "warn";
  return "neutral";
};

const fmtDateTime = (d) =>
  d && moment.utc(d).isValid() ? moment.utc(d).format("YYYY-MM-DD HH:mm:ss") : "";

const csvBtnSx = { textTransform: "none", fontSize: 12, color: tokens.textDim, borderColor: tokens.border, "&:hover": { borderColor: tokens.border2 } };

const ActionList = () => {
  const [isLoading, setIsLoading] = useState(true);
  const [rows, setRows] = useState([]);
  const [filterText, setFilterText] = useState("");
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);

  useEffect(() => {
    fetchUserData();
  }, []);

  const fetchUserData = async () => {
    let url = `${endPoints.api.GET_Action_List}`;
    await getAllData(url)
      .then((response) => {
        setIsLoading(false);
        if (response?.status === "success") {
          setRows(response.data);
        }
      })
      .catch((error) => {
        setIsLoading(false);
        console.error(error);
      });
  };

  const exportReport = (format) => {
    const body = { userId: 0, startdate: "", enddate: "" };
    fetch(`${endPoints.apiBaseUrl + endPoints.api.ACTION_REPORT + "/" + format}`, {
      method: "POST",
      body: JSON.stringify(body),
      headers: { "Content-Type": "application/json" },
    }).then(function (response) {
      response.blob().then((blob) => {
        const link = document.createElement("a");
        link.href = URL.createObjectURL(blob);
        link.download = `ActionListReport.${format}`;
        link.click();
      });
    });
  };

  const filtered = filterText
    ? rows.filter((item) => {
        const q = filterText.toLowerCase();
        return (
          String(item.login || "").toLowerCase().includes(q) ||
          String(item.eventType || "").toLowerCase().includes(q) ||
          String(item.eventName || "").toLowerCase().includes(q) ||
          String(item.idUser || "").includes(q)
        );
      })
    : rows;

  return (
    <Box>
      {isLoading && <Loader spinner={true} visible={isLoading} />}

      <PageHeader
        title="Action List"
        crumb="Users / Action List"
        subtitle="Player events · logins, registrations, deposits"
      />

      <SectionCard>
        <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1.5, alignItems: "center", mb: 2 }}>
          <TextField
            size="small"
            placeholder="Filter by login, event or user ID"
            value={filterText}
            onChange={(e) => {
              setFilterText(e.target.value);
              setPage(0);
            }}
            sx={{ minWidth: 260, "& .MuiInputBase-root": { fontSize: 13 } }}
          />
          <Box sx={{ ml: "auto", display: "flex", gap: 1 }}>
            <Button size="small" variant="outlined" startIcon={<FileDownloadOutlinedIcon />} sx={csvBtnSx} onClick={() => exportReport("pdf")}>
              PDF
            </Button>
            <Button size="small" variant="outlined" startIcon={<FileDownloadOutlinedIcon />} sx={csvBtnSx} onClick={() => exportReport("csv")}>
              CSV
            </Button>
          </Box>
        </Box>

        <Box sx={{ overflowX: "auto" }}>
          <Box component="table" sx={{ width: "100%", borderCollapse: "collapse" }}>
            <thead>
              <tr>
                <th style={th}>Event Type</th>
                <th style={th}>Event Name</th>
                <th style={th}>Insert At</th>
                <th style={th}>User ID</th>
                <th style={th}>Fbclid</th>
                <th style={th}>Login</th>
              </tr>
            </thead>
            <tbody>
              {filtered.length === 0 && !isLoading && (
                <tr>
                  <td colSpan={6} style={{ ...td, textAlign: "center", color: tokens.textDim }}>
                    No events found
                  </td>
                </tr>
              )}
              {filtered
                .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                .map((row, i) => (
                  <tr key={`${row.idUser}-${row.insertionTime}-${i}`}>
                    <td style={td}>{row.eventType ? pill(row.eventType, eventKind(row.eventType)) : ""}</td>
                    <td style={{ ...td, fontWeight: 600 }}>{row.eventName}</td>
                    <td style={{ ...td, fontFamily: mono, color: tokens.textDim }}>{fmtDateTime(row.insertionTime)}</td>
                    <td style={{ ...td, fontFamily: mono }}>{row.idUser}</td>
                    <td style={{ ...td, fontFamily: mono, color: tokens.textDim2, maxWidth: 220, overflow: "hidden", textOverflow: "ellipsis" }}>
                      {row.fbclid || "—"}
                    </td>
                    <td style={{ ...td, fontWeight: 600 }}>{row.login}</td>
                  </tr>
                ))}
            </tbody>
          </Box>
        </Box>

        <TablePagination
          component="div"
          rowsPerPageOptions={[10, 25, 50, 100]}
          count={filtered.length}
          rowsPerPage={rowsPerPage}
          page={page}
          SelectProps={{ inputProps: { "aria-label": "rows per page" }, native: true }}
          onPageChange={(e, newPage) => setPage(newPage)}
          onRowsPerPageChange={(e) => {
            setRowsPerPage(parseInt(e.target.value, 10));
            setPage(0);
          }}
          ActionsComponent={TablePaginationActions}
          sx={{ borderTop: `1px solid ${tokens.border}`, mt: 1 }}
        />
      </SectionCard>
    </Box>
  );
};

export default ActionList;
